import { ButtonPrimary, Heading4, Table, TextM } from 'app/components';
import { TableBodyCellContainer } from 'app/styles/components/Table';
import { formatCurrency } from 'app/utils/normalizer';
import { formatDateFromBackend } from 'app/helpers/dateHelpers';
import Accordion from 'app/components/Accordion/Accordion';
import { DisbursementDatesSelector, ReviewStepBox } from '../../styles';
import { CopyIcon } from 'app/components/Icons';
import { useTheme } from '@mui/material';
import CreditSimulatorSchedule from '../../../SimulationV2Step/CreditSimulatorV2/CreditSimulatorSchedule';
import { CustomSelect } from 'app/components/CustomSelect';
import { FinalSimulation, Simulation } from '../../utils/borrowerTypeParser';
import { TProducts } from 'modules/products/context';
import {
  TSimulatorV2Request,
  TSimulatorV2Response,
} from 'app/services/simulatorHttpServiceV2';

interface ReviewStepInstallmentsAccordionProps {
  isOpen: boolean;
  onClick: () => void;
  isNewSimulator: boolean;
  product: TProducts | null;
  simulation: Simulation | null;
  finalSimulation: FinalSimulation | null;
  simulationV2Request: TSimulatorV2Request | null;
  simulationV2: TSimulatorV2Response | null;
  originalSimulationV2RequestRef: React.MutableRefObject<TSimulatorV2Request | null>;
  handleDisbursementDateChangeV2: (date: string) => void;
  simulateWithDifferentDisbursementDate: (date: string) => void;
  baseIofPerInstallment: number;
  simulationLoading: boolean;
  handleCopySchedule: () => void;
  tableHeadContent: Array<{
    id: string;
    content: React.ReactNode | null;
  }>;
  dateOptions: Array<{
    label: string;
    value: string;
  }>;
  selectedDateValue:
    | {
        label: string;
        value: string;
      }
    | undefined;
  onDateChange: (value: string) => void;
}

const ReviewStepInstallmentsAccordion = ({
  isOpen,
  onClick,
  isNewSimulator,
  product,
  simulation,
  finalSimulation,
  simulationV2Request,
  simulationV2,
  originalSimulationV2RequestRef,
  handleDisbursementDateChangeV2,
  simulateWithDifferentDisbursementDate,
  baseIofPerInstallment,
  simulationLoading,
  handleCopySchedule,
  tableHeadContent,
  dateOptions,
  selectedDateValue,
  onDateChange,
}: ReviewStepInstallmentsAccordionProps) => {
  const theme = useTheme();

  return (
    <ReviewStepBox marginTop="16px" marginBottom="16px" padding="24px 8px">
      <Accordion
        cleanStyle={true}
        isOpen={isOpen}
        title={<Heading4>Parcelas</Heading4>}
        onClick={onClick}
      >
        {!isNewSimulator &&
        product?.multiple_disbursements_dates &&
        simulation ? (
          <DisbursementDatesSelector>
            <CustomSelect
              labelValue="Selecione a data de desembolso"
              options={dateOptions}
              handleInputChange={(evt) => {
                onDateChange(evt.currentTarget.value?.value);
              }}
              isClearable={false}
              value={selectedDateValue}
              name="disbursementScheduleDate"
              placeholder="Selecionar"
            />
          </DisbursementDatesSelector>
        ) : (
          <CreditSimulatorSchedule
            noHeader
            product={product}
            simulationRequest={
              originalSimulationV2RequestRef.current || simulationV2Request
            }
            simulationResponse={simulationV2}
            simulateWithDifferentDisbursementDate={
              isNewSimulator
                ? handleDisbursementDateChangeV2
                : simulateWithDifferentDisbursementDate
            }
          />
        )}
        <div style={{ marginTop: '15px' }}>
          {finalSimulation?.schedule &&
          Array.isArray(finalSimulation?.schedule) &&
          finalSimulation?.schedule.length > 0 ? (
            <>
              <Table
                maxBodyHeight="55vh"
                tableHeadSticky={true}
                tableHeadCell={tableHeadContent}
                isLoading={simulationLoading}
                tableBodyContent={finalSimulation.schedule.map(
                  (simulationData, inx) => {
                    const iof = baseIofPerInstallment + simulationData.iof;
                    return {
                      id: String(inx),
                      cells: [
                        {
                          id: '1',
                          content: (
                            <TableBodyCellContainer>
                              <TextM style={{ margin: '0 auto' }} weight="Bold">
                                {inx + 1}
                              </TextM>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '2',
                          content: (
                            <TableBodyCellContainer>
                              <TextM>
                                {formatCurrency(simulationData.payment)}
                              </TextM>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '3',
                          content: (
                            <TableBodyCellContainer>
                              <TextM>
                                {formatCurrency(simulationData.principal)}
                              </TextM>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '4',
                          content: (
                            <TableBodyCellContainer>
                              <TextM>
                                {formatCurrency(simulationData.interest)}
                              </TextM>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '5',
                          content: (
                            <TableBodyCellContainer>
                              <TextM>{formatCurrency(iof)}</TextM>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '6',
                          content: (
                            <TableBodyCellContainer>
                              <TextM>
                                {formatDateFromBackend(
                                  simulationData.payment_date,
                                ).toLocaleDateString('pt-BR')}
                              </TextM>
                            </TableBodyCellContainer>
                          ),
                        },
                      ],
                    };
                  },
                )}
                tableFooterCell={[
                  {
                    id: '1',
                    content: null,
                  },
                  {
                    id: '2',
                    content: (
                      <TableBodyCellContainer>
                        <div style={{ display: 'block' }}>
                          <div>
                            <TextM>Total</TextM>
                          </div>
                          <div>
                            <TextM weight="Bold">
                              {formatCurrency(
                                finalSimulation.schedule
                                  .map((schedule) => schedule.payment)
                                  .reduce((a, b) => a + b, 0),
                              )}
                            </TextM>
                          </div>
                        </div>
                      </TableBodyCellContainer>
                    ),
                  },
                  {
                    id: '3',
                    content: (
                      <TableBodyCellContainer>
                        <div style={{ display: 'block' }}>
                          <div>
                            <TextM>Total</TextM>
                          </div>
                          <div>
                            <TextM weight="Bold">
                              {formatCurrency(
                                finalSimulation.schedule
                                  .map((schedule) => schedule.principal)
                                  .reduce((a, b) => a + b, 0),
                              )}
                            </TextM>
                          </div>
                        </div>
                      </TableBodyCellContainer>
                    ),
                  },
                  {
                    id: '4',
                    content: (
                      <TableBodyCellContainer>
                        <div style={{ display: 'block' }}>
                          <div>
                            <TextM>Total</TextM>
                          </div>
                          <div>
                            <TextM weight="Bold">
                              {formatCurrency(
                                finalSimulation.schedule
                                  .map((schedule) => schedule.interest)
                                  .reduce((a, b) => a + b, 0),
                              )}
                            </TextM>
                          </div>
                        </div>
                      </TableBodyCellContainer>
                    ),
                  },
                  {
                    id: '5',
                    content: (
                      <TableBodyCellContainer>
                        <div style={{ display: 'block' }}>
                          <div>
                            <TextM>Total</TextM>
                          </div>
                          <div>
                            <TextM weight="Bold">
                              {formatCurrency(
                                finalSimulation.schedule
                                  .map(
                                    (schedule) =>
                                      baseIofPerInstallment + schedule.iof,
                                  )
                                  .reduce((a, b) => a + b, 0),
                              )}
                            </TextM>
                          </div>
                        </div>
                      </TableBodyCellContainer>
                    ),
                  },
                  {
                    id: '6',
                    content: null,
                  },
                ]}
              />
              <ButtonPrimary
                style={{
                  backgroundColor: 'transparent',
                  width: 'fit-content',
                  margin: '15px auto 0px',
                }}
                typeVariant="outline"
                btnIcon={
                  <CopyIcon
                    color={theme.palette.brand.primary.base as string}
                  />
                }
                iconPosition="left"
                onClick={handleCopySchedule}
              >
                <TextM
                  weight="Semibold"
                  style={{
                    marginLeft: '11px',
                    color: theme.palette.brand.primary.base,
                  }}
                >
                  Copiar resultado
                </TextM>
              </ButtonPrimary>
            </>
          ) : !isNewSimulator ? (
            <TextM>
              Nenhuma parcela disponível. Conclua primeiro a simulação no step
              de valores da solicitação.
            </TextM>
          ) : undefined}
        </div>
      </Accordion>
    </ReviewStepBox>
  );
};

export default ReviewStepInstallmentsAccordion;
